import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NgControl,
} from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatRadioChange } from '@angular/material/radio';
import { identifier } from '@uipath/angular/utilities';

import { Subject } from 'rxjs';

@Component({
    selector: 'ui-toggle-switch',
    templateUrl: './ui-toggle-switch.component.html',
    styleUrls: ['ui-toggle-switch.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: UiToggleSwitchComponent,
        },
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UiToggleSwitchComponent),
            multi: true,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class UiToggleSwitchComponent implements
    OnInit,
    OnDestroy,
    MatFormFieldControl<boolean>,
    ControlValueAccessor {

    @Input() defaultValue = true;
    @Input() labelText = 'Value';
    @Input() onString = 'True';
    @Input() offString = 'False';
    @Input() disabled = false;
    @Input() currentValue: boolean | null = null;

    @HostBinding('class.floating')
    public shouldLabelFloat = false;
    public id: string;
    public stateChanges = new Subject<void>();
    public ngControl: NgControl | null = null;
    public focused = false;
    public shouldPlaceholderFloat = false;
    public errorState = false;
    public describedBy = '';

    @Output() optionChange = new EventEmitter<boolean>();

    private _randomName: string;
    private _required = false;

    constructor(
        private _cd: ChangeDetectorRef,
    ) {
        this._randomName = identifier();
        this.id = 'toggle-switch-' + this._randomName;
    }

    ngOnInit() {
        this.currentValue = this.defaultValue;
    }

    ngOnDestroy() {
        this.stateChanges.complete();
    }

    @Input()
    get value(): boolean | null {
        return this.currentValue;
    }
    set value(state: boolean | null) {
        this.currentValue = state;
        this.stateChanges.next();
    }

    @Input()
    get placeholder() {
        return this.labelText;
    }
    set placeholder(plh) {
        this.labelText = plh;
        this.stateChanges.next();
    }

    get empty() {
        return this.currentValue == null;
    }

    @Input()
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = req;
        this.stateChanges.next();
    }

    writeValue(value: boolean) {
        this.currentValue = value;
        this._cd.markForCheck();
        this.stateChanges.next();
    }

    registerOnChange(fn: (ev: MatRadioChange) => void) {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this._onTouch = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
        this._cd.detectChanges();
    }

    setDescribedByIds(ids: string[]) {
        this.describedBy = ids.join(' ');
    }

    onContainerClick(_event: MouseEvent) { }

    public onChange(state: MatRadioChange) {
        this.writeValue(state.value);
        this._onChange(state.value);
        this.optionChange.emit(state.value);
    }

    public onClick() {
        this._onTouch();
    }

    private _onChange = (_value: MatRadioChange) => { };
    private _onTouch = () => { };
}
